import axios from 'axios';
import router from '@/router';
import store from '@/store';
import uuidv4 from 'uuid/v4';

export const AUTH_API_HOST = process.env.VUE_APP_TOKEN_API_URL;

const credentialsMap = {
  [process.env.VUE_APP_OMEGA_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY_CLIENT_CREDENTIALS_OMEGA || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET_CLIENT_CREDENTIALS_OMEGA || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `OMEGA-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  },
  [process.env.VUE_APP_TESYS_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY_CLIENT_CREDENTIALS_TESYS || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET_CLIENT_CREDENTIALS_TESYS || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `TESYS-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  },
  [process.env.VUE_APP_GESIURIS_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY_CLIENT_CREDENTIALS_GESIURIS || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET_CLIENT_CREDENTIALS_GESIURIS || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `GESIURIS-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  },
  [process.env.VUE_APP_VALENTUM_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY_CLIENT_CREDENTIALS_VALENTUM || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET_CLIENT_CREDENTIALS_VALENTUM || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `VALENTUM-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  },
  [process.env.VUE_APP_INNO_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `INNOCAPITAL-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  },
  [process.env.VUE_APP_METAGESTION_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY_CLIENT_CREDENTIALS_METAGESTION || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET_CLIENT_CREDENTIALS_METAGESTION || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `METAGESTION-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  },
  [process.env.VUE_APP_MAGALLANES_URI]: {
    consumer_key: process.env.VUE_APP_CONSUMER_KEY_CLIENT_CREDENTIALS_MAGALLANES || '',
    consumer_secret: process.env.VUE_APP_CONSUMER_SECRET_CLIENT_CREDENTIALS_MAGALLANES || '',
    user_prefix:
      process.env.VUE_APP_ENV !== 'local'
        ? `MAGALLANES-${process.env.VUE_APP_USERSTORE_PREFIX}`
        : process.env.VUE_APP_USERSTORE_PREFIX
  }
};

const getApiCredentials = () => {
  const currentUrl = window.location.href;
  const hostname = new URL(currentUrl).hostname;
  let credentials;

  if (process.env.VUE_APP_ENV === 'local') {
    const clientName = process.env.VUE_APP_LOCAL_CLIENT_NAME;

    const consumerKeyVar = `VUE_APP_CONSUMER_KEY_${clientName}`;
    const consumerSecretVar = `VUE_APP_CONSUMER_SECRET_${clientName}`;

    credentials = {
      consumer_key: process.env[consumerKeyVar],
      consumer_secret: process.env[consumerSecretVar],
      user_prefix: process.env.VUE_APP_USERSTORE_PREFIX
    };
  } else {
    credentials = credentialsMap[hostname];
    if (!credentials) {
      throw new Error(`No API credentials found for hostname: ${hostname}`);
    }
  }

  return {
    consumer_key: credentials.consumer_key,
    consumer_secret: credentials.consumer_secret,
    user_prefix: credentials.user_prefix
  };
};
/**
 * @override
 */
function generateBasicAuthorization() {
  const { consumer_key, consumer_secret } = getApiCredentials();
  const token = btoa(`${consumer_key}:${consumer_secret}`);
  return `Basic ${token}`;
}

const CONFIG = {
  baseURL: AUTH_API_HOST,
  withCredentials: false,
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: `${generateBasicAuthorization()}`
  }
};

const getAccessToken = payload => {
  const params = new URLSearchParams();
  const { grant_type } = payload;
  params.append('grant_type', grant_type);
  if (grant_type === 'password') {
    const { user_prefix } = getApiCredentials();
    params.append('username', `${user_prefix}/${payload.username}@carbon.super`);
    params.append('password', payload.password);
  } else if (grant_type === 'refresh_token') {
    params.append('refresh_token', payload.refresh_token);
  } else {
    params.append('scope', '');
    params.append('code', payload.code);
    params.append('redirect_uri', process.env.VUE_APP_REDIRECT_URI);
  }
  return axios.post('/oauth2/token', params, CONFIG);
};

const revokeToken = () => {
  const params = new URLSearchParams();
  params.append('token', atob(localStorage.getItem('BOCOAT')));
  return axios.post('oauth2/revoke', params, CONFIG);
};

export const isAuthorized = () => {
  const now = Date.now();
  const accessToken = localStorage.getItem('BOCOAT');
  const expiresIn = localStorage.getItem('BOCOEI');
  if (!accessToken || !expiresIn) {
    return false;
  }
  const dExpiresIn = Number(atob(expiresIn));
  if (now > dExpiresIn) {
    return false;
  }
  return true;
};

export const tryToRefreshToken = () => {
  const now = Date.now();
  const accessToken = localStorage.getItem('BOCOAT');
  const expiresIn = localStorage.getItem('BOCOEI');
  if (!accessToken || !expiresIn) {
    return Promise.reject(new Error('No hay session'));
  }
  const dExpiresIn = Number(atob(expiresIn));
  if (now > dExpiresIn) {
    const refresh_token = atob(localStorage.getItem('BOCORT'));
    const payload = {
      grant_type: 'refresh_token',
      refresh_token
    };
    return refreshToken(payload);
  } else {
    return Promise.resolve('OK');
  }
};

export const authenticate = payload => {
  if (isAuthorized()) {
    return Promise.resolve();
  }
  return getAccessToken(payload).then(response => {
    saveAuthData(response.data);
  });
};

export const authenticate365 = payload => {
  if (isAuthorized()) {
    return Promise.resolve();
  }
  return getAccessToken(payload).then(response => {
    console.warn(response);
    saveAuthData(response.data);
  });
};

export const refreshToken = payload => {
  return getAccessToken(payload).then(response => saveAuthData(response.data));
};

export const authorizationInterceptor = async config => {
  config.headers['X-Request-Id'] = uuidv4();
  const publicPaths = ['/login', '/password-reset', '/new-password', '/login-third-party', '/login/office365'];

  try {
    await tryToRefreshToken();
  } catch (err) {
    console.warn(err);
    store.dispatch('yuriMenuStore/reset');
    store.dispatch('formStore/deleteAllForms');
    const currentPath = window.location.pathname; // Ruta actual

    if (!publicPaths.includes(currentPath)) {
      // Solo redirigir si la ruta no es pública
      store.dispatch('yuriMenuStore/reset');
      store.dispatch('formStore/deleteAllForms');
      router.replace(`/login`);
    }
  }
  if (isAuthorized()) {
    const prefix = atob(localStorage.getItem('BOCOTT'));
    const token = atob(localStorage.getItem('BOCOAT'));
    config.headers.Authorization = `${prefix} ${token}`;
  }
  return config;
};

export const authorizationInterceptorOnError = err => {
  return Promise.reject(err);
};

export const saveAuthData = data => {
  return new Promise(resolve => {
    localStorage.setItem('BOCOAT', btoa(data.access_token));
    const expires_in = Date.now() + data.expires_in * 1000;
    localStorage.setItem('BOCOEI', btoa(expires_in));
    localStorage.setItem('BOCORT', btoa(data.refresh_token));
    localStorage.setItem('BOCOTT', btoa(data.token_type));
    resolve({
      ...data,
      ...{ expires_in }
    });
  });
};

export const removeAuthData = () => {
  return revokeToken().then(() => {
    localStorage.clear();
    return true;
  });
};

export const onCloseEventAuthHandler = () => {
  const x = Number(localStorage.getItem('BOCOT'));
  if (x > 1) {
    localStorage.setItem('BOCOT', x - 1);
  } else {
    removeAuthData();
  }
  return 'cerrar sesion';
};

export const onOpenEventAuthHandler = () => {
  let x = Number(localStorage.getItem('BOCOT'));
  if (!x) {
    x = 0;
  }
  localStorage.setItem('BOCOT', x + 1);
};

export default {
  authenticate,
  authenticate365,
  authorizationInterceptor,
  authorizationInterceptorOnError,
  isAuthorized,
  tryToRefreshToken,
  refreshToken,
  removeAuthData
};
